/**
 * 本地数据库相关操作
 */

import { PadInfo, OfflineData } from '@tencent/docs_offline';
import { DOCUMENT_STATUS, DOCUMENT_TYPE, DOCUMENT_DATA_TYPE, TABLE_NAME } from '@tencent/ark-model';
import { QUERY_CONDITION_TYPE, QueryCondition } from '@tencent/tdoc-database';
import { type ILogger } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { IOfflineEditContext } from '../core/interfaces/offline-edit-context.interace';
import { IPadInfoData, IOfflineDBService, ICreateFileResult } from '../core/interfaces/offline-db-service.interface';
import DBHelper, { IndexDBStatus, OperationType } from '../managers/db-helper';
import OfflineApiManager from '../managers/offline-api-manager';
import { PadIdType } from '../models/padInfo';

const MODULE_NAME = '[ODBS]';

export class OfflineDBService implements IOfflineDBService {
    private dbHelper: DBHelper;
    private createFileSuccess: boolean;

    public constructor(
        private context: IOfflineEditContext,
        private logger: ILogger,
    ) {
        this.dbHelper = new DBHelper(this.logger);
        this.createFileSuccess = false;
    }

    /**
     * 数据库相关初始化结束
     * @returns
     */
    public waitForDBManagerReady = async () => {
        if (this.dbHelper.DBstatus === IndexDBStatus.InitFinish) {
            this.logger.info(MODULE_NAME, 'already init finish');
            return true;
        }
        try {
            const dbStatus = await this.dbHelper.DBStatusPromise;
            this.logger.info(MODULE_NAME, 'waitForDBManagerReady success', dbStatus);
            return true;
        } catch (err) {
            this.logger.tdw?.({
                opername: 'quality_slide',
                module: 'offline-edit',
                action: 'client-db-open-error',
                ver6: err?.toString?.(),
            });
            this.logger.error(MODULE_NAME, `waitForDBManagerReady error: ${err?.toString?.()}`);
            return false;
        }
    };

    /**
     * 上锁
     * @returns
     */
    public lock = async () => {
        try {
            const lockResult = await this.dbHelper.lock(this.context.secretId);
            if (!lockResult.isSuccess) {
                throw new Error('lock fail');
            }
            this.logger.info(MODULE_NAME, 'lock success');
            return true;
        } catch (err) {
            this.logger.info(MODULE_NAME, 'lock fail', err?.toString());
            return false;
        }
    };

    /**
     * 释放锁
     * @returns
     */
    public release = async () => {
        try {
            await this.dbHelper.disConnect();
            const releaseResult = await this.dbHelper.release(this.context.secretId);
            this.logger.info(MODULE_NAME, 'release', releaseResult);
            return releaseResult;
        } catch (error) {
            this.logger.error(MODULE_NAME, 'release catch error:', error);
            return false;
        }
    };

    /**
     * 生成文档数据 & padInfo 信息表
     * 品类需判断数据库是否已存在当前 padInfo 信息，存在则只能 updateFiled 更新
     * 否则会覆盖列表页数据库
     * @param documentData
     * @param padInfo
     */
    public updateMultiTableData = async (
        documentData: OfflineData,
        padInfo: PadInfo,
    ) => {
        try {
            const padInfoFromDB = await this.getPadInfo(this.context.globalPadId);
            const documentDataToDB = this.updateDocumentData(documentData);
            const padInfoToDB = this.updatePadInfo({
                padInfo,
                isAlreadyExist: !!padInfoFromDB,
            });
            const [documentRes, padInfoRes] = await Promise.all([documentDataToDB, padInfoToDB]);
            return {
                error: documentRes.error || padInfoRes.error || '',
            };
        } catch (err) {
            return {
                error: err?.toString?.() ?? 'error',
                data: null,
            };
        }
    };

    /**
     * 根据 secretId / globalPadId 查询文档 padInfo信息
     * @param id
     * @param docIdType
     * @returns
     */
    public getPadInfo = async (id: string, docIdType: PadIdType = PadIdType.GLOBAL): Promise<PadInfo | null> => {
        try {
            const condition = QueryCondition.newInstance()
                .addCondition('uid', QUERY_CONDITION_TYPE.ONLY, this.context.uid);
            switch (docIdType) {
                case PadIdType.GLOBAL:
                    condition.addCondition('globalPadId', QUERY_CONDITION_TYPE.ONLY, id);
                    break;
                case PadIdType.SECRET:
                    condition.addCondition('secretId', QUERY_CONDITION_TYPE.ONLY, id);
                    break;
            }
            const queryResult = await this.dbHelper.handleDataFromIndexDB<OperationType.get>({
                tableName: TABLE_NAME.padinfo,
                type: OperationType.get,
                where: condition,
            });
            let parseData = queryResult.data;
            try {
                if (typeof parseData === 'string') {
                    parseData = JSON.parse(parseData);
                }
            } catch (error) {
                this.logger.error(MODULE_NAME, 'getPadInfo parse catch error:', error);
            }
            return queryResult.error ? null : parseData as PadInfo;
        } catch (err) {
            this.logger.error(MODULE_NAME, 'getPadInfo catch error: ', err);
            return null;
        }
    };

    /**
     * 获取文档 clientVars 数据
     * @returns
     */
    public getClientVarsFromDB = async () => {
        try {
            const { uid, secretId } = this.context;
            const condition = QueryCondition.newInstance()
                .addCondition('secretId', QUERY_CONDITION_TYPE.ONLY, secretId)
                .addCondition('type', QUERY_CONDITION_TYPE.IN, [DOCUMENT_DATA_TYPE.init])
                .addCondition('uid', QUERY_CONDITION_TYPE.ONLY, uid);
            const result = await this.dbHelper.handleDataFromIndexDB<OperationType.get>({
                type: OperationType.get,
                tableName: TABLE_NAME.documentData,
                where: condition,
            });
            return {
                error: result.error,
                data: result.data?.data,
            };
        } catch (err) {
            this.logger.error(MODULE_NAME, 'getClientVarsFromDB catch error: ', err);
            return {
                error: err?.toString?.() ?? 'error',
                data: null,
            };
        }
    };

    /**
     * 同步远端创建文档
     * @returns
     */
    public createFile = async (): Promise<ICreateFileResult> => {
        if (this.createFileSuccess) {
            return {
                error: 'createFile is already success, no duplicate create',
            };
        }
        const { secretId4New, folder4New } = this.context;
        // 创建文档 - sdk 内包含创建成功后的 padInfo 和 documentData 数据更新
        const { isSuccess, clientVars, reason } = await (await OfflineApiManager.offlineApi())
            .createOnlineDocumentToDetail(
                secretId4New,
                DOCUMENT_TYPE.slide,
                folder4New,
            );
        this.createFileSuccess = !!isSuccess;
        return {
            error: isSuccess ? '' : (reason || 'error'),
            data: clientVars,
        };
    };

    /**
     * 更新本地文档数据
     * @param data
     */
    public updateDocumentData = async (data: OfflineData) => {
        const { uid, secretId } = this.context;
        const condition = QueryCondition.newInstance()
            .addCondition('secretId', QUERY_CONDITION_TYPE.ONLY, secretId)
            .addCondition('type', QUERY_CONDITION_TYPE.IN, [DOCUMENT_DATA_TYPE.init])
            .addCondition('uid', QUERY_CONDITION_TYPE.ONLY, uid);
        this.logger.info(MODULE_NAME, 'ready to updateDocumentData');
        try {
            const res = await this.dbHelper.handleDataFromIndexDB<OperationType.update>({
                type: OperationType.update,
                tableName: TABLE_NAME.documentData,
                data,
                where: condition,
            });
            if (res.error) {
                throw res.error;
            }
            return res;
        } catch (err) {
            return {
                error: err?.toString() ?? 'error',
                data: null,
            };
        }
    };

    /**
     * 更新文档 padInfo 数据，本地不存在则新存储
     * @param padInfoData
     */
    public updatePadInfo = async (padInfoData: IPadInfoData) => {
        const { uid, globalPadId } = this.context;
        let condition;
        if (padInfoData.isAlreadyExist) {
            condition = new QueryCondition()
                .addCondition('globalPadId', QUERY_CONDITION_TYPE.ONLY, globalPadId)
                .addCondition('uid', QUERY_CONDITION_TYPE.ONLY, uid);
        }
        // 确保新创建成功的文档，不会因为数据库异常将文档状态更改为 new
        if (this.createFileSuccess && (padInfoData.padInfo as any).status !== DOCUMENT_STATUS.normal) {
            padInfoData.padInfo.status = DOCUMENT_STATUS.normal as any;
        }
        try {
            const res = await this.dbHelper.handleDataFromIndexDB<OperationType.updateField>({
                type: condition ? OperationType.updateField : OperationType.update,
                tableName: TABLE_NAME.padinfo,
                data: padInfoData.padInfo,
                where: condition,
            });
            if (res.error) {
                throw res.error;
            }
            return res;
        } catch (err) {
            return {
                error: err?.toString() ?? 'error',
                data: null,
            };
        }
    };


    /**
     * 更新/删除 离线数据
     * @param data
     */
    public updateOfflineData = async (data?: OfflineData) => {
        const { uid, secretId, globalPadId } = this.context;
        const condition = QueryCondition.newInstance()
            .addCondition('globalPadId', QUERY_CONDITION_TYPE.ONLY, globalPadId)
            .addCondition('secretId', QUERY_CONDITION_TYPE.ONLY, secretId)
            .addCondition('type', QUERY_CONDITION_TYPE.IN, [DOCUMENT_DATA_TYPE.committing, DOCUMENT_DATA_TYPE.waiting, DOCUMENT_DATA_TYPE.queue])
            .addCondition('uid', QUERY_CONDITION_TYPE.ONLY, uid);
        try {
            if (data) {
                // 更新离线数据
                const res = await this.dbHelper.handleDataFromIndexDB<OperationType.update>({
                    type: OperationType.update,
                    tableName: TABLE_NAME.offlineData,
                    data,
                    where: condition,
                });
                if (res.error) {
                    throw res.error;
                }
                return res;
            }
            // 删除离线数据
            const res = await this.dbHelper.handleDataFromIndexDB<OperationType.delete>({
                type: OperationType.delete,
                tableName: TABLE_NAME.offlineData,
                where: condition,
            });
            if (res.error) {
                throw res.error;
            }
            return res;
        } catch (err) {
            return {
                error: err?.toString?.() ?? 'error',
                data: null,
            };
        }
    };

    /**
     * 获取尚未提交的本地离线数据
     * @returns
     */
    public getUnCommittedOfflineData = async () => {
        const { secretId } = this.context;
        try {
            const dataResult = await this.dbHelper.getUnCommittedOfflineData();
            if (!Array.isArray(dataResult.data)) {
                throw new Error('getUnCommittedOfflineData data is null');
            }
            const dataArray: OfflineData[] = dataResult.data.filter(item => (item.secretId === secretId));
            return {
                error: '',
                data: dataArray[0] ?? null,
            };
        } catch (err) {
            return {
                error: err?.toString?.() || 'error',
                data: null,
            };
        }
    };

    /**
     * 手动更新本地文档离线标志
     * @param manualOffline
     * @returns
     */
    public updateIsManualOffline = async (manualOffline: boolean) => {
        const { uid, globalPadId } = this.context;
        const condition = QueryCondition.newInstance()
            .addCondition('globalPadId', QUERY_CONDITION_TYPE.ONLY, globalPadId)
            .addCondition('uid', QUERY_CONDITION_TYPE.ONLY, uid);
        const data = { isManualOffline: manualOffline };
        this.logger.info(MODULE_NAME, 'updateIsManualOffline');
        try {
            const res = await this.dbHelper.handleDataFromIndexDB<OperationType.updateField>({
                type: OperationType.updateField,
                tableName: TABLE_NAME.padinfo,
                where: condition,
                data,
            });
            if (res.error) {
                throw res.error;
            }
            return res;
        } catch (err) {
            return {
                error: err?.toString?.() ?? 'error',
                data: null,
            };
        }
    };

    /**
     * 获取设置手动离线的数据量
     */
    public getAllManualOfflinePadInfo = async () => {
        const condition = QueryCondition.newInstance()
            .addCondition('uid', QUERY_CONDITION_TYPE.ONLY, this.context.uid)
            .addCondition('isManualOffline', QUERY_CONDITION_TYPE.ONLY, true);
        try {
            const res = await this.dbHelper.handleDataFromIndexDB<OperationType.getAll>({
                type: OperationType.getAll,
                tableName: TABLE_NAME.padinfo,
                where: condition,
            });
            if (res.error) {
                throw res.error;
            }
            return res;
        } catch (err) {
            return {
                error: err?.toString() ?? 'error',
                data: null,
            };
        }
    };
}
